import React, { useRef, useState } from 'react';
import { Upload, X, Image as ImageIcon, Loader2 } from 'lucide-react';

// Convert file to base64 data URL
function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

// Resize/compress image via canvas
async function compressImage(file, maxDim = 512, quality = 0.85) {
  const dataUrl = await fileToDataUrl(file);
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      let { width, height } = img;
      if (width > height && width > maxDim) {
        height = (height * maxDim) / width;
        width = maxDim;
      } else if (height > maxDim) {
        width = (width * maxDim) / height;
        height = maxDim;
      }
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, width, height);
      const isPng = file.type === 'image/png';
      resolve(canvas.toDataURL(isPng ? 'image/png' : 'image/jpeg', quality));
    };
    img.src = dataUrl;
  });
}

export default function LogoUpload({ label, value, onChange, maxDim = 512, aspect = 'auto', accept = 'image/*' }) {
  const inputRef = useRef(null);
  const [loading, setLoading] = useState(false);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLoading(true);
    try {
      const compressed = await compressImage(file, maxDim, 0.9);
      onChange(compressed);
    } finally {
      setLoading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const clear = () => onChange('');

  return (
    <div>
      <label className="block text-sm font-semibold text-gray-700 mb-2">{label}</label>
      {value ? (
        <div className="relative border border-gray-200 rounded-xl p-4 bg-gray-50">
          <img
            src={value}
            alt={label}
            className={`mx-auto object-contain bg-white rounded-lg ${aspect === 'square' ? 'w-24 h-24' : 'h-16 max-w-full'}`}
          />
          <div className="flex gap-2 mt-3">
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              className="flex-1 py-2 text-xs font-semibold text-japo-green border border-japo-green rounded-lg hover:bg-rose-50"
            >
              Ganti
            </button>
            <button
              type="button"
              onClick={clear}
              className="py-2 px-3 text-xs font-semibold text-red-600 border border-red-200 rounded-lg hover:bg-red-50"
              aria-label="Hapus"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={loading}
          className="w-full border-2 border-dashed border-gray-200 rounded-xl p-6 text-center text-sm text-gray-500 hover:border-japo-green hover:bg-rose-50/20 transition-colors flex flex-col items-center gap-2"
        >
          {loading ? (
            <>
              <Loader2 className="w-6 h-6 text-japo-green animate-spin" />
              <span>Memproses...</span>
            </>
          ) : (
            <>
              <div className="w-10 h-10 rounded-full bg-japo-green/10 flex items-center justify-center">
                <Upload className="w-5 h-5 text-japo-green" />
              </div>
              <span className="font-semibold">Klik untuk upload</span>
              <span className="text-xs text-gray-400">PNG, JPG, SVG (max ~2MB)</span>
            </>
          )}
        </button>
      )}
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        onChange={handleFile}
        className="hidden"
      />
    </div>
  );
}
